import {
  API,
  ImagesAPIPayloadProps,
  RequestAPIPayloadProps,
} from 'constants/api'
import { PAGE_PATH } from 'constants/pagePath'
import {
  CONTRACT_INFO_FORM,
  VEHICLE_STATE,
  VEHICLE_STATE_FORM,
} from 'constants/qnState'
import { QN_STATUS } from 'constants/qnStatus'
import { useEffect } from 'react'
import {
  AccidentStatusTypes,
  CargoQuantityTypes,
  CargoTypes,
  DisplacementTypes,
  EngineTroubleStatusTypes,
  FallingWheelStatusTypes,
  FieldTypes,
  FlatTireCauseTypes,
  FuelTypes,
  HighwayLocationTypes,
  LocationTypes,
  LossKeyStatusTypes,
  OthersStatusTypes,
  QnStateProps,
  RelationshipTypes,
  SnowDetailTypes,
  SpareTireStatusTypes,
  StorageLocationTypes,
  VehicleTypes,
} from 'states/QnState'
import deepcopy from 'ts-deepcopy'

import { useSender } from './axios'
import { convertWideKanaToHalfKana, hasSelectedImage } from './commonUtils'
import {
  addCollAccPlcCd,
  addDestinationPayload,
  addPuncPlcNormCd,
  addReservationPayload,
  addTroubleDetail,
  addTroubleDetailBoolItem,
  addTroubleDetailSelectItem,
  addTroubleLocMark,
  addVehicleHeightModificationPayload,
  addVehiclePartCd,
  formatSendAddress,
  getImagePayload,
} from './qnState'
import {
  fail,
  getStatusCode,
  isFail,
  isSuccess,
  mergeStatus,
  StatusProps,
  success,
} from './status'
import { isSaficDomain } from './domain'
import { useEditingItemStateKey, useQNStateKey } from './commonHooks'
import { JwtContext } from '../contexts/JwtContext'
import React from 'react'
import { DestinationTypes } from '../states/QnState'
import { isDestinationTarget } from './qnState'
import dayjs from 'dayjs'

export const useSendImageAction = (
  state: QnStateProps
): (() => Promise<StatusProps>) => {
  const { vehicleStateInfo } = state
  const [, sendImages] = useSender(API.IMAGES)
  const sendImageAction = async (): Promise<StatusProps> => {
    type imagesProps = {
      hasImage: boolean
      payload: ImagesAPIPayloadProps
    }

    // 要請者写真登録のpayload構築
    const images = ((): imagesProps => {
      const resultImages: imagesProps = {
        hasImage: false,
        payload: {},
      }

      switch (state.vehicleState) {
        // 事故、落輪
        case VEHICLE_STATE.ACCIDENT:
        case VEHICLE_STATE.FALLING_WHEEL:
          resultImages.hasImage = hasSelectedImage(
            vehicleStateInfo.accidentImage
          )
          getImagePayload(resultImages.payload, vehicleStateInfo.accidentImage)
          break
        // その他
        case VEHICLE_STATE.OTHERS:
          if (
            vehicleStateInfo.othersStatus.includes(OthersStatusTypes.snow) ||
            vehicleStateInfo.othersStatus.includes(OthersStatusTypes.others)
          ) {
            // 雪・その他のみ
            resultImages.hasImage = hasSelectedImage(
              vehicleStateInfo.othersImage
            )
            getImagePayload(resultImages.payload, vehicleStateInfo.othersImage)
          }
          break
      }
      return resultImages
    })()

    return images.hasImage ? await sendImages(images.payload) : success()
  }
  return sendImageAction
}

export const useSendRequestAction = (
  state: QnStateProps
): (() => Promise<StatusProps>) => {
  const { contractInfo, locationStateInfo, vehicleStateInfo } = state
  const [, sendRequest] = useSender(API.REQUEST)
  const sendRequestAction = async (): Promise<StatusProps> => {
    // 要請者入力情報連携のpayload構築
    const requestPayload = ((): RequestAPIPayloadProps => {
      const input = deepcopy(state)

      // 要請者情報のinputから要請者画像のデータを削除する
      Object.values(input.vehicleStateInfo.accidentImage).forEach(
        (imageObj) => (imageObj.image = '')
      )
      Object.values(input.vehicleStateInfo.othersImage).forEach(
        (imageObj) => (imageObj.image = '')
      )

      const payload: RequestAPIPayloadProps = {
        contactContentCd: '0',
        troubleDetailContent: '',
        troubleLocTypeCd: '0',
        troubleLocLocation: '',
        troubleLocLatitude: '0',
        troubleLocLongitude: '0',
        input: JSON.stringify(input),
      }

      // お車の場所
      payload.troubleLocLocation = formatSendAddress(state.location.address)
      payload.troubleLocLatitude = String(state.location.lat)
      payload.troubleLocLongitude = String(state.location.lng)

      // 周辺情報
      // トラブル詳細内容（追記）：場所種別
      addTroubleDetail(
        payload,
        VEHICLE_STATE_FORM.locationType.formName,
        VEHICLE_STATE_FORM.locationType.options[locationStateInfo.locationType]
      )
      switch (locationStateInfo.locationType) {
        // 一般道
        case LocationTypes.localLoad: {
          // 場所種別：一般道
          payload.troubleLocTypeCd = '1'
          // 目印／進行方向：補足内容
          addTroubleLocMark(payload, locationStateInfo.localLoadSupplement)
          // トラブル詳細内容（追記）：補足内容
          addTroubleDetail(
            payload,
            VEHICLE_STATE_FORM.localLoadSupplement.formName,
            locationStateInfo.localLoadSupplement
          )
          break
        }
        // 高速道路
        case LocationTypes.highway: {
          // 場所種別：一般道・SA/PA
          payload.troubleLocTypeCd = locationStateInfo.highwayLocation.includes(
            HighwayLocationTypes.mainLine
          )
            ? '2'
            : '3'
          // トラブル詳細内容（追記）：停車場所
          addTroubleDetailSelectItem(
            payload,
            VEHICLE_STATE_FORM.highwayLocation,
            locationStateInfo.highwayLocation
          )
          // 目印／進行方向：補足内容
          addTroubleLocMark(payload, locationStateInfo.highwaySupplement)
          // トラブル詳細内容（追記）：補足内容
          addTroubleDetail(
            payload,
            VEHICLE_STATE_FORM.highwaySupplement.formName,
            locationStateInfo.highwaySupplement
          )
          break
        }
        // 車庫・駐車場
        case LocationTypes.home: {
          // 場所種別：車庫・駐車場 自宅・通常保管場所
          payload.troubleLocTypeCd = '5'
          // 停車場所の特徴
          if (locationStateInfo.homeStorageLocation.length > 0) {
            payload.troubleLocParkingCd = {
              [StorageLocationTypes.streetParking]: '1',
              [StorageLocationTypes.selfDriving]: '2',
              [StorageLocationTypes.mechanical]: '3',
            }[locationStateInfo.homeStorageLocation[0]]
            // トラブル詳細内容（追記）：停車場所の特徴
            addTroubleDetailSelectItem(
              payload,
              VEHICLE_STATE_FORM.homeStorageLocation,
              locationStateInfo.homeStorageLocation[0]
            )
          }
          // 目印／進行方向：補足内容
          addTroubleLocMark(payload, locationStateInfo.homeStorageSupplement)
          // トラブル詳細内容（追記）：補足内容
          addTroubleDetail(
            payload,
            VEHICLE_STATE_FORM.homeStorageSupplement.formName,
            locationStateInfo.homeStorageSupplement
          )
          break
        }
        // 上記以外の駐車場
        case LocationTypes.garageParking:
          // 場所種別：車庫・駐車場 上記以外
          payload.troubleLocTypeCd = '6'
          // 停車場所の特徴
          if (locationStateInfo.otherStorageLocation.length > 0) {
            payload.troubleLocParkingCd = {
              [StorageLocationTypes.streetParking]: '1',
              [StorageLocationTypes.selfDriving]: '2',
              [StorageLocationTypes.mechanical]: '3',
            }[locationStateInfo.otherStorageLocation[0]]
            // トラブル詳細内容（追記）：停車場所の特徴
            addTroubleDetailSelectItem(
              payload,
              VEHICLE_STATE_FORM.otherStorageLocation,
              locationStateInfo.otherStorageLocation[0]
            )
          }
          // 目印／進行方向：補足内容
          addTroubleLocMark(payload, locationStateInfo.otherStorageSupplement)
          // トラブル詳細内容（追記）：補足内容
          addTroubleDetail(
            payload,
            VEHICLE_STATE_FORM.otherStorageSupplement.formName,
            locationStateInfo.otherStorageSupplement
          )
          break
        // 舗装されていない道路
        case LocationTypes.noPavedLoad:
          // 場所種別：舗装されていない道路
          payload.troubleLocTypeCd = '4'
          // 補足内容
          addTroubleLocMark(payload, locationStateInfo.noPavedLoadSupplement)
          // トラブル詳細内容（追記）：補足内容
          addTroubleDetail(
            payload,
            VEHICLE_STATE_FORM.noPavedLoadSupplement.formName,
            locationStateInfo.noPavedLoadSupplement
          )
          break
        // その他
        case LocationTypes.other: {
          // 場所種別：その他
          payload.troubleLocTypeCd = '7'
          // 目印／進行方向：補足内容
          addTroubleLocMark(payload, locationStateInfo.otherSupplement)
          // トラブル詳細内容（追記）：補足内容
          addTroubleDetail(
            payload,
            VEHICLE_STATE_FORM.otherSupplement.formName,
            locationStateInfo.otherSupplement
          )
          break
        }
      }

      // トラブル発生日時
      payload.troubleOccurDateTime =
        state.troubleDatetime.timing === 'earlier'
          ? dayjs(state.troubleDatetime.date).format('YYYY/MM/DD/HH')
          : VEHICLE_STATE_FORM.troubleDatetime.options[
              state.troubleDatetime.timing
            ]
      VEHICLE_STATE_FORM.troubleDatetime.options.earlier =
        state.troubleDatetime.timing === 'earlier' ? '上記以前' : ''
      addTroubleDetailSelectItem(
        payload,
        VEHICLE_STATE_FORM.troubleDatetime,
        state.troubleDatetime.timing
      )

      // お車の状況
      addTroubleDetail(payload, 'お車の状況', state.vehicleState)
      switch (state.vehicleState) {
        // バッテリー・エンジン関連
        case VEHICLE_STATE.ENGINE_TROUBLE:
          payload.contactContentCd = '3'
          // 状況
          payload.troubleContentCd = {
            [EngineTroubleStatusTypes.engineNotStart]: '1',
            [EngineTroubleStatusTypes.battery]: '2',
            [EngineTroubleStatusTypes.engineStop]: '3',
            [EngineTroubleStatusTypes.overheat]: '8',
          }[vehicleStateInfo.engineTroubleStatus]
          // トラブル詳細内容（追記）：状況
          addTroubleDetailSelectItem(
            payload,
            VEHICLE_STATE_FORM.engineTroubleStatus,
            vehicleStateInfo.engineTroubleStatus
          )
          // 希望する対応
          if (
            vehicleStateInfo.engineTroubleStatus.includes(
              EngineTroubleStatusTypes.engineNotStart
            )
          ) {
            payload.desiredResponse =
              VEHICLE_STATE_FORM.troubleSupport.options[
                vehicleStateInfo.troubleSupport
              ]
          }
          break
        // パンク
        case VEHICLE_STATE.FLAT_TIRE:
          payload.contactContentCd = '4'
          // 状況：パンク固定
          payload.troubleContentCd = '6'
          // パンク箇所
          addPuncPlcNormCd(payload, state)
          // スペアタイヤの有無
          payload.puncSpareTireExistCd = {
            [SpareTireStatusTypes.yes]: '1',
            [SpareTireStatusTypes.none]: '2',
            [SpareTireStatusTypes.unknown]: '99',
          }[vehicleStateInfo.spareTireStatus]
          // トラブル詳細内容（追記）：スペアタイヤの有無
          addTroubleDetailSelectItem(
            payload,
            VEHICLE_STATE_FORM.spareTireStatus,
            vehicleStateInfo.spareTireStatus
          )
          // パンク原因
          payload.backgroundOfThePuncture = {
            [FlatTireCauseTypes.accident]: '1',
            [FlatTireCauseTypes.deterioration]: '2',
            [FlatTireCauseTypes.unknown]: '99',
          }[vehicleStateInfo.flatTireCause]
          // トラブル詳細内容（追記）：パンク原因
          addTroubleDetailSelectItem(
            payload,
            VEHICLE_STATE_FORM.flatTireCause,
            vehicleStateInfo.flatTireCause
          )
          break
        // 事故
        case VEHICLE_STATE.ACCIDENT:
          payload.contactContentCd = '1'
          // 状況
          switch (vehicleStateInfo.accidentStatus) {
            case AccidentStatusTypes.car:
              payload.troubleContentCd = '12'
              payload.accTypeCd = '1'
              break
            case AccidentStatusTypes.object:
              payload.troubleContentCd = '13'
              payload.accTypeCd = '5'
              break
            case AccidentStatusTypes.rollover:
              payload.troubleContentCd = '16'
              break
            case AccidentStatusTypes.falling:
              payload.troubleContentCd = '15'
          }
          // トラブル詳細内容（追記）：状況
          addTroubleDetailSelectItem(
            payload,
            VEHICLE_STATE_FORM.accidentStatus,
            vehicleStateInfo.accidentStatus
          )
          // トラブル詳細内容（追記）：写真以外で状況説明
          addTroubleDetailBoolItem(
            payload,
            VEHICLE_STATE_FORM.noPhoto,
            vehicleStateInfo.noPhoto
          )
          // 破損個所【複】：選択箇所
          addCollAccPlcCd(payload, state)
          break
        // 落輪・乗り上げ
        case VEHICLE_STATE.FALLING_WHEEL:
          payload.contactContentCd = '2'
          // 状況
          switch (vehicleStateInfo.fallingWheelStatus) {
            case FallingWheelStatusTypes.falling:
              payload.troubleContentCd = '17'
              payload.dropLocationCd = {
                [FieldTypes.gutter]: '1',
                [FieldTypes.curb]: undefined,
                [FieldTypes.others]: '3',
              }[vehicleStateInfo.fieldType.type]
              // 落輪個所【複】：選択箇所
              addVehiclePartCd(
                payload,
                VEHICLE_STATE_FORM.fallingWheelPart,
                state
              )
              break
            case FallingWheelStatusTypes.rideup:
              payload.troubleContentCd = '14'
              payload.rideLocationCd = {
                [FieldTypes.gutter]: undefined,
                [FieldTypes.curb]: '2',
                [FieldTypes.others]: '3',
              }[vehicleStateInfo.fieldType.type]
              // 乗り上げ個所【複】：選択箇所
              addVehiclePartCd(
                payload,
                VEHICLE_STATE_FORM.rideupWheelPart,
                state
              )
              break
          }
          // トラブル詳細内容（追記）：状況
          addTroubleDetailSelectItem(
            payload,
            VEHICLE_STATE_FORM.fallingWheelStatus,
            vehicleStateInfo.fallingWheelStatus
          )
          // トラブル詳細内容（追記）：現場状況
          addTroubleDetailSelectItem(
            payload,
            VEHICLE_STATE_FORM.fieldType,
            vehicleStateInfo.fieldType.type,
            vehicleStateInfo.fieldType.type.includes(FieldTypes.others)
              ? vehicleStateInfo.fieldType.others
              : undefined
          )
          // トラブル詳細内容（追記）：写真以外で状況説明
          addTroubleDetailBoolItem(
            payload,
            VEHICLE_STATE_FORM.noPhoto,
            vehicleStateInfo.noPhoto
          )
          break
        // 燃料切れ
        case VEHICLE_STATE.EMPTY_FUEL:
          payload.contactContentCd = '5'
          // トラブル内容：燃料切れ
          payload.troubleContentCd = '4'
          // 油種
          payload.outGasOilTypeCd = {
            [FuelTypes.regular]: '1',
            [FuelTypes.highOctane]: '2',
            [FuelTypes.lightOil]: '3',
            [FuelTypes.electricity]: '4',
          }[vehicleStateInfo.fuelType]
          // トラブル詳細内容（追記）：油種
          addTroubleDetailSelectItem(
            payload,
            VEHICLE_STATE_FORM.fuelType,
            vehicleStateInfo.fuelType
          )
          break
        // カギの紛失
        case VEHICLE_STATE.LOSS_KEY:
          payload.contactContentCd = '6'
          // 状況
          payload.troubleContentCd = {
            [LossKeyStatusTypes.insideCar]: '5',
            [LossKeyStatusTypes.loss]: '11',
          }[vehicleStateInfo.lossKeyStatus]
          // トラブル詳細内容（追記）：状況
          addTroubleDetailSelectItem(
            payload,
            VEHICLE_STATE_FORM.lossKeyStatus,
            vehicleStateInfo.lossKeyStatus
          )
          break
        // その他
        case VEHICLE_STATE.OTHERS:
          payload.contactContentCd = '7'
          // 状況
          payload.troubleContentCd = {
            [OthersStatusTypes.snow]: '9',
            [OthersStatusTypes.oil]: '7',
            [OthersStatusTypes.overheat]: '8',
            [OthersStatusTypes.others]: '10',
          }[vehicleStateInfo.othersStatus]
          // トラブル詳細内容（追記）：状況
          addTroubleDetailSelectItem(
            payload,
            VEHICLE_STATE_FORM.othersStatus,
            vehicleStateInfo.othersStatus
          )
          if (
            isSaficDomain() &&
            vehicleStateInfo.othersStatus.includes(OthersStatusTypes.snow)
          ) {
            // SAFICで雪・泥・砂の場合は詳細状況を設定する
            payload.insuranceType = {
              [SnowDetailTypes.snowWithNotNormal]: '3',
              [SnowDetailTypes.snowWithNormal]: '4',
              [SnowDetailTypes.mudAndSand]: '5',
            }[vehicleStateInfo.snowDetail]
            // トラブル詳細内容（追記）：詳細状況
            addTroubleDetailSelectItem(
              payload,
              VEHICLE_STATE_FORM.snowDetail,
              vehicleStateInfo.snowDetail
            )
          }
          // 補足内容
          if (vehicleStateInfo.othersDetail) {
            payload.requester2Remarks = `【トラブル内容「その他」が選択されています。】／${vehicleStateInfo.othersDetail}`
          }
          // トラブル詳細内容（追記）：補足内容
          addTroubleDetail(
            payload,
            VEHICLE_STATE_FORM.othersDetail.formName,
            vehicleStateInfo.othersDetail
          )
          break
      }

      // 車高改造系のpayloadを追加
      addVehicleHeightModificationPayload(payload, state)

      // 搬送先系のpayloadを追加
      addDestinationPayload(payload, state)

      // 自走できない場合の運び先候補を表示する対象のトラブル画面を選択かつ、工場紹介を選択した場合、requester2Remarksに文言を追加
      if (
        isDestinationTarget(state) &&
        state.vehicleStateInfo.destinationType === DestinationTypes.introduction
      ) {
        payload.requester2Remarks = payload.requester2Remarks
          ? payload.requester2Remarks.concat(
              '、',
              '【工場紹介を希望しているお客さまです】'
            )
          : '【工場紹介を希望しているお客さまです】'
      }

      // 要請者名
      payload.requesterNameKana = convertWideKanaToHalfKana(
        contractInfo.nameKana
      )
      addTroubleDetail(
        payload,
        CONTRACT_INFO_FORM.nameKana.formName,
        contractInfo.nameKana
      )
      // 電話番号
      payload.tel = contractInfo.phoneNumber
      addTroubleDetail(
        payload,
        CONTRACT_INFO_FORM.phoneNumber.formName,
        contractInfo.phoneNumber
      )
      // 種別
      payload.kinds = {
        [RelationshipTypes.self]: '1',
        [RelationshipTypes.family]: '2',
        [RelationshipTypes.friend]: '3',
        [RelationshipTypes.employee]: '6',
        [RelationshipTypes.other]: '10',
      }[contractInfo.relationship]
      addTroubleDetailSelectItem(
        payload,
        CONTRACT_INFO_FORM.relationship,
        contractInfo.relationship
      )
      // 車体登録番号
      payload.bodyRegistrationNum = convertWideKanaToHalfKana(
        contractInfo.plateNumber
      )
      addTroubleDetail(
        payload,
        CONTRACT_INFO_FORM.plateNumber.formName,
        contractInfo.plateNumber
      )
      // 車両種別
      addTroubleDetailSelectItem(
        payload,
        CONTRACT_INFO_FORM.vehicleType,
        contractInfo.vehicleType
      )
      switch (contractInfo.vehicleType) {
        // 普通
        case VehicleTypes.autoMobile:
          payload.vehicleType = '1'
          break
        // トラック
        case VehicleTypes.truck:
          payload.vehicleType = {
            [CargoTypes.lightTruck]: '2',
            [CargoTypes.under2t]: '3',
            [CargoTypes.under4t]: '4',
            [CargoTypes.over4t]: '5',
            [CargoTypes.over10t]: '6',
          }[contractInfo.cargo]
          // 積載量の詳細
          addTroubleDetailSelectItem(
            payload,
            CONTRACT_INFO_FORM.cargo,
            contractInfo.cargo
          )
          if (contractInfo.cargo !== 'lightTruck')
            // 積荷
            payload.cargo = {
              [CargoQuantityTypes.over50per]: '1',
              [CargoQuantityTypes.under50per]: '2',
              [CargoQuantityTypes.full]: '3',
              [CargoQuantityTypes.none]: '4',
            }[contractInfo.cargoQuantity]
          addTroubleDetailSelectItem(
            payload,
            CONTRACT_INFO_FORM.cargoQuantity,
            contractInfo.cargoQuantity
          )
          break
        // バス
        case VehicleTypes.bus:
          payload.vehicleType = '7'
          break
        // バイク
        case VehicleTypes.motorbike:
          payload.vehicleType = {
            [DisplacementTypes.under50]: '8',
            [DisplacementTypes.under125]: '9',
            [DisplacementTypes.under250]: '10',
            [DisplacementTypes.under400]: '11',
            [DisplacementTypes.over401]: '12',
          }[contractInfo.bikeDisplacement]
          // 排気量の詳細
          addTroubleDetailSelectItem(
            payload,
            CONTRACT_INFO_FORM.bikeDisplacement,
            contractInfo.bikeDisplacement
          )
          break
        // その他
        case VehicleTypes.other:
          payload.vehicleType = '99'
          break
      }
      // JAF会員確認
      payload.jafMembershipConfirmation = contractInfo.jafMembershipConfirmation
        ? '1'
        : '2'
      addTroubleDetailBoolItem(
        payload,
        CONTRACT_INFO_FORM.jafMembershipConfirmation,
        contractInfo.jafMembershipConfirmation
      )
      if (contractInfo.jafMembershipConfirmation) {
        // 会員番号
        payload.jafMembershipNumber = contractInfo.jafMembershipNumber
        addTroubleDetail(
          payload,
          CONTRACT_INFO_FORM.jafMembershipNumber.formName,
          contractInfo.jafMembershipNumber
        )
        // 会員名
        if (contractInfo.jafMembershipName) {
          payload.jafMembershipName = contractInfo.jafMembershipName
          addTroubleDetail(
            payload,
            CONTRACT_INFO_FORM.jafMembershipName.formName,
            contractInfo.jafMembershipName
          )
        }
      }

      // 予約情報のpayloadを追加
      addReservationPayload(payload, state)

      // troubleDetailContentの最大文字数が344文字なので、超えていた場合はカットする
      /** troubleDetailContentの最大文字数 */
      const MAX_LENGTH_TROUBLE_DETAIL_CONTENT = 344
      payload.troubleDetailContent = payload.troubleDetailContent.substring(
        0,
        MAX_LENGTH_TROUBLE_DETAIL_CONTENT
      )
      return payload
    })()

    return await sendRequest(requestPayload)
  }
  return sendRequestAction
}

export const useSendApi = (
  sendImageAction: () => Promise<StatusProps>,
  sendRequestAction: () => Promise<StatusProps>
) => {
  return async (): Promise<StatusProps> => {
    const sendResults: Array<StatusProps> = []

    sendResults.push(await sendImageAction())

    if (isFail(sendResults[sendResults.length - 1])) {
      return sendResults[sendResults.length - 1]
    }

    sendResults.push(await sendRequestAction())

    return mergeStatus(...sendResults)
  }
}

type ResultStatus = {
  data: Array<any>
  messages: Array<string>
  success: boolean
}

export const useSendAction = (
  errorModal: boolean,
  setLoadingModal: React.Dispatch<React.SetStateAction<boolean>>,
  setAlertModal: React.Dispatch<React.SetStateAction<boolean>>,
  sendApi: () => Promise<StatusProps>,
  setErrorCount: React.Dispatch<React.SetStateAction<number>>,
  errorCount: number,
  history: any,
  setSendSuccess: React.Dispatch<React.SetStateAction<boolean>>,
  setState: (state: QnStateProps) => StatusProps,
  state: QnStateProps
): (() => StatusProps | Promise<StatusProps>) => {
  const [, setJwt] = React.useContext(JwtContext)
  const [, getMaintenanceStatus] = useSender(API.MAINTENANCE)
  return async (): Promise<StatusProps> => {
    if (errorModal) {
      return fail()
    }

    setLoadingModal(true)
    setAlertModal(false)

    const resultStatus: ResultStatus = await sendApi()

    let statusCode: number | undefined = undefined
    let request: StatusProps | undefined

    if (
      resultStatus.success ||
      resultStatus.data.some((apiData) => apiData?.response)
    ) {
      // [要請者写真登録, 要請者入力情報連携]
      const [, requestTemp]: Array<StatusProps> = (await Array.isArray(
        resultStatus
      ))
        ? resultStatus.data
        : resultStatus.data

      statusCode = getStatusCode(resultStatus)
      request = requestTemp
    }

    if (!statusCode || statusCode === 503) {
      const { client, from, line, params } = state.urlParameters
      const hasMaintenanceStatus = async (): Promise<boolean> => {
        const maintenanceStatus: StatusProps = await getMaintenanceStatus({})
        if (Number(maintenanceStatus?.data?.data?.status) > 0) {
          /** QNのパラメータ */
          const searchParams = new URLSearchParams(
            params
              ? {
                  client,
                  from,
                  line,
                  params,
                }
              : {
                  client,
                  from,
                  line,
                }
          ).toString()

          /** ローカルストレージ削除 */
          window.localStorage.removeItem(useQNStateKey)
          window.localStorage.removeItem(useEditingItemStateKey)

          /** QNメンテナンス画面に遷移 */
          const maintenanceDomain =
            process.env.NODE_ENV === 'development'
              ? `http://${window.location.hostname}:3001` // 開発モードでメンテナンス画面に遷移したい場合、rak-front-maintenanceのポートを3001で起動すること
              : `${window.location.origin}/maintenance/index.html`

          window.location.href = `${maintenanceDomain}?${searchParams}`
          return true
        }
        return false
      }
      if (await hasMaintenanceStatus()) {
        return fail()
      }
    }

    if (statusCode === 500) {
      setErrorCount(errorCount + 1)
    } else if (isFail(resultStatus)) {
      history.push(PAGE_PATH.ERROR)
    }

    if (isSuccess(resultStatus)) {
      // WebSocketの起動
      setJwt(request?.data.jwt) // C-1(要請者情報連携機能)をcallすると新しく発行されたjwtが発行されるため、ローカルストレージのjwtの更新を行う

      setSendSuccess(true)

      setState({
        ...state,
        qnStatus:
          state.reservation.date && state.reservation.time
            ? QN_STATUS.RESERVED
            : QN_STATUS.INFORMED,
      })
    }

    setLoadingModal(false)
    return resultStatus
  }
}

export const useWatchError = (
  errorCount: number,
  setErrorModal: React.Dispatch<React.SetStateAction<boolean>>,
  setState: (state: QnStateProps) => StatusProps,
  state: QnStateProps,
  setAlertModal: React.Dispatch<React.SetStateAction<boolean>>
): void => {
  useEffect(() => {
    if (errorCount >= 3) {
      setErrorModal(true)
      setState({
        ...state,
        sendError: true,
      })
    } else if (errorCount >= 1) {
      setAlertModal(true)
    }
  }, [errorCount])
}
